import { Contract } from 'ethers';

import SecuritizationPoolValueServiceJSON from '@/lib/abi/SecuritizationPoolValueService.json';
import { rpcProviderService } from '@/services/rpc-provider/rpc-provider.service';
import { configService } from '@/services/config/config.service';
import { Address } from '@/types';
import _, { isArray, isEmpty } from 'lodash';
import { Invoice, Loan } from '@/services/pool/types';
import ERC20ABI from '@/lib/abi/ERC20.json';

export class SecuritizationPoolValueService {
  constructor(
    private readonly abi = SecuritizationPoolValueServiceJSON.abi,
    public readonly address = configService.network.addresses.untangled
      ?.securitizationPoolValueService,
    private readonly provider = rpcProviderService.jsonProvider
  ) {}

  /**
   * @summary Instantiates a contract instance for the securitization pool value service.
   * @returns Ethers Contract instance
   */
  public getInstance(): Contract {
    if (!this.address) throw new Error('No bb-a-USD address');
    return new Contract(this.address, this.abi, this.provider);
  }

  /**
   * @description This function returns the asset value.
   * This starts at 1 when the pool is created and grows over time. Because of preminted BPT, it uses `
   */
  public async getExpectedAssetValues(
    poolContractAddress: Address,
    tokenIds: string[]
  ): Promise<any[]> {
    const instance = this.getInstance();
    try {
      const value = await instance.getDebtAssetValues(
        poolContractAddress,
        tokenIds
      );

      return value;
    } catch (e) {
      return [];
    }
  }

  public async getAssetInterestRates(
    poolContractAddress: Address,
    tokenIds: string[]
  ): Promise<any[]> {
    const instance = this.getInstance();
    const value = await instance.getAssetInterestRates(
      poolContractAddress,
      tokenIds
    );

    return value;
  }

  public async getLoanAssetsValues(
    assets: Loan[],
    poolContract: Address,
    decimals: number
  ) {
    if (isEmpty(assets) || !isArray(assets)) return [];

    const result = _.flatten(
      await Promise.all(
        _.chunk(assets, 60).map(assets_chunk => {
          try {
            const tokenIds = assets_chunk.map(i => i.tokenId).filter(i => i);

            // @ts-ignore
            return this.getExpectedAssetValues(poolContract, tokenIds).then(
              result => {
                return result.map(i =>
                  _.round(+i.toString() / 10 ** decimals, decimals)
                );
              }
            );
          } catch (e) {
            return 0;
          }
        })
      )
    );
    let count = 0;

    return assets.map(item => {
      if (item.tokenId) return result[count++];
      return 0;
    });
  }

  public async getInvoiceAssetsValues(assets: Invoice[], decimals: number) {
    const result = _.flatten(
      await Promise.all(
        _.chunk(assets, 60).map(assets_chunk => {
          const tokenIds = assets_chunk.map(i => i.aitTokenId).filter(i => i);

          // @ts-ignore
          return this.getExpectedAssetValues(tokenIds).then(r => {
            return r.map(i =>
              _.round(+i.toString() / 10 ** decimals, decimals)
            );
          });
        })
      )
    );
    let count = 0;

    return assets.map(item => {
      if (item.aitTokenId) return result[count++];
      return 0;
    });
  }

  public async getInvoiceInterestRates(
    poolContractAddress: Address,
    assets: Invoice[]
  ) {
    const tokenIds = assets.map(i => i.aitTokenId).filter(i => i);

    const result = await this.getAssetInterestRates(
      poolContractAddress,
      tokenIds
    ).then(result => result.map(i => _.round(+i.toString() / 10000, 2)));

    let count = 0;

    return assets.map(item => {
      if (item.aitTokenId) return result[count++];
      return 0;
    });
  }
  public async getLoanInterestRates(
    poolContractAddress: Address,
    assets: Loan[]
  ) {
    if (isEmpty(assets) || !isArray(assets)) return [];

    const tokenIds = assets.map(i => i.tokenId).filter(i => i);

    const result = await this.getAssetInterestRates(
      poolContractAddress,
      // @ts-ignore
      tokenIds
    )
      .then(result => {
        console.log(result);
        return result.map(i => _.round(+i.toString() / 10000, 2));
      })
      .catch(e => {
        console.log('error', { e });
      });

    let count = 0;

    return assets.map(item => {
      if (item.tokenId) return result[count++];
      return 0;
    });
  }

  public async getMultiNoteTokenAssetsBalance(assets: any[]) {
    return Promise.all(
      assets.map(async asset => {
        const [JOTprice, SOTprice] = await Promise.all([
          await securitizationPoolValueService.getJOTTokenPrice(
            asset.poolEvent.poolId
          ),
          await securitizationPoolValueService.getSOTTokenPrice(
            asset.poolEvent.poolId
          ),
        ]);
        const JOTcontract = new Contract(
          asset.poolEvent.juniorNotesTokenAddress,
          ERC20ABI,
          rpcProviderService.jsonProvider
        );
        const SOTcontract = new Contract(
          asset.poolEvent.notesTokenAddress,
          ERC20ABI,
          rpcProviderService.jsonProvider
        );
        const [JOTbalance, SOTbalance] = await Promise.all([
          await JOTcontract.balanceOf(asset.investor),
          await SOTcontract.balanceOf(asset.investor),
        ]);
        return {
          principal: JOTbalance.mul(JOTprice)
            .add(SOTbalance.mul(SOTprice))
            .toString(),
          interest: 0,
        };
      })
    );
  }

  public async getJOTTokenPrice(poolContractAddress: Address): Promise<any> {
    try {
      const instance = this.getInstance();
      const value = await instance.getJOTTokenPrice(poolContractAddress);
      return value;
    } catch (error) {
      // console.error(error);
      return 0;
    }
  }

  public async getTokenPrices(
    poolContractAddresses: Address[],
    tokenAddresses: Address[]
  ): Promise<any> {
    try {
      const instance = this.getInstance();
      const value = await instance.getTokenPrices(
        poolContractAddresses,
        tokenAddresses
      );
      return value;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  public async getSOTTokenPrice(poolContractAddress: Address): Promise<any> {
    try {
      const instance = this.getInstance();
      const value = await instance.getSOTTokenPrice(poolContractAddress);
      return value;
    } catch (error) {
      // console.error(error);
      return 0;
    }
  }

  public async getExternalTokenInfos(
    poolContractAddress: Address
  ): Promise<any> {
    try {
      const instance = this.getInstance();
      const value = await instance.getExternalTokenInfos(poolContractAddress);
      return value;
    } catch (error) {
      // console.error(error);
      return [];
    }
  }
}

export const securitizationPoolValueService =
  new SecuritizationPoolValueService();
